/**
 * The read contract: the serialisable shape one organisation's system map
 * crosses HTTP in.
 *
 * ── WHY THE MAP IS ORG-SCOPED AND NOT ROLE-SCOPED ───────────────────────────
 *
 * A Role owns its resources exclusively, so a Role-scoped map could only ever
 * show arrows that stay inside one Role — and those are the arrows nobody needs
 * a map to understand. The whole value is the arrow that CROSSES: a workflow in
 * the Support Role invoking an AI task in the Billing Role. That arrow does not
 * exist in either Role's own view. So the map is assembled for the org and each
 * node carries `roleId`, which is what makes a crossing visible at all.
 */
import { z } from "zod";

import { DerivedEdgeSchema } from "./edge.types";
import type { SystemMapIntegrity } from "./system-map-integrity.types";
import { RoleSystemNodeTypeSchema } from "./system-node.types";

/** Why a node's edge set is not the whole story. */
export const SystemMapNodeGapSchema = z.enum([
  /** Contains a node running arbitrary code — 55.9% of workflows in production. */
  "OPAQUE_CODE_NODE",
  /** Contains a node type that has no edge rule. */
  "UNCLASSIFIED_NODE_TYPE",
  /** Its stored nodes could not be read at all. */
  "NODES_UNREADABLE"
]);

export type SystemMapNodeGap = z.infer<typeof SystemMapNodeGapSchema>;

/** One thing on the map. */
export const RoleSystemMapNodeSchema = z.object({
  type: RoleSystemNodeTypeSchema,
  id: z.string(),
  /**
   * The Role that owns this resource, or null.
   *
   * Null means one of two different things and the consumer can tell them apart
   * with `isRoleOwnableSystemNodeType`: an ownable type with no Role is
   * UNASSIGNED, while an unownable type (a collection, the workspace) can never
   * have one and is not a gap in anyone's configuration.
   */
  roleId: z.string().nullable(),
  /**
   * This node names a row from a catalog SHARED across every organisation, so
   * no organisation owns it and no Role can hold it.
   *
   * 🚨 THE FIELD EXISTS BECAUSE `roleId: null` CARRIES TWO OPPOSITE MEANINGS FOR
   * ONE TYPE, AND ONLY THE SERVER CAN TELL THEM APART. `external_tool` is a
   * Role-ownable type, so a consumer reading `isRoleOwnableSystemNodeType` alone
   * files EVERY unheld tool under UNASSIGNED — "a gap somebody should close". For
   * a marketplace plugin that is false: `AgentExternalTool.organizationId IS NULL`
   * is the deliberate shared Pipedream/Apify catalog, `AssignResource` refuses it
   * (`FindExternalToolByIdPrismaAdapter` is strict-org, correctly), and the org's
   * own skills list never offered it. The client cannot derive any of that from a
   * type and an id, so the map said "Add to role" over a control that always 404s.
   *
   * TRUE means population SHARED: draw it, never offer to file it. Only ever true
   * for `external_tool` today, and it is a property of the ROW rather than of the
   * viewer — so it is the same answer for every caller, which is what lets it sit
   * on a view schema at all.
   */
  sharedCatalog: z.boolean(),
  /** Display name where one is cheaply available. Nullable, never optional. */
  label: z.string().nullable(),
  /**
   * The provider's brand mark, where the resource's own row carries one.
   * Nullable, never optional — the same convention as `label`, and for the same
   * reason: a consumer must handle the absence, so it may not be a key that is
   * simply missing.
   *
   * 🚨 NULL IS THE COMMON CASE AND IS NOT A FAILURE. Only `external_tool` has an
   * icon column at all (`AgentExternalTool.imageUrl`); a workflow, an agent, a
   * deployment, an AI task, a collection and a workspace have nowhere to read one
   * from and are permanently null here. Even among tools it is null for a row
   * whose `imageUrl` is unset — 127 of 1,420 org-owned rows in production on
   * 2026-08-12. So a reader draws its own placeholder for null and never treats
   * it as a gap in anyone's configuration.
   *
   * It travels with `label`, on the same read, deliberately: both are filled by
   * whichever door names the node, so a node can never arrive named-but-iconless
   * through one door and complete through another.
   */
  iconUrl: z.string().nullable(),
  /**
   * False when this node MAY have arrows that are not on the map.
   *
   * Only ever false for a workflow — every other node type emits no edges of
   * its own, so its edge set is trivially complete. A workflow reading false
   * must be rendered as "incomplete", never as isolated: 327 of 585 production
   * workflows are in this state, so it is the common case and its copy is the
   * copy most users will read.
   */
  edgeSetComplete: z.boolean(),
  /** Why, when `edgeSetComplete` is false. Empty otherwise. */
  gaps: z.array(SystemMapNodeGapSchema)
});

export type RoleSystemMapNode = z.infer<typeof RoleSystemMapNodeSchema>;

/** One arrow on the map: a derived edge, plus the workflow it left. */
export const RoleSystemMapEdgeSchema = DerivedEdgeSchema.extend({
  /** The workflow whose node drew this. The `from` end of every arrow is a workflow. */
  fromWorkflowId: z.string()
});

export type RoleSystemMapEdge = z.infer<typeof RoleSystemMapEdgeSchema>;

const SystemMapWarningSubjectSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("workflow"), id: z.string() }),
  z.object({ kind: z.literal("node"), workflowId: z.string(), nodeId: z.string() }),
  z.object({ kind: z.literal("organization") })
]);

export const SystemMapWarningSchema = z.object({
  code: z.enum([
    "WORKFLOW_NODES_UNREADABLE",
    "NODE_TYPE_UNCLASSIFIED",
    "EDGE_SET_INCOMPLETE",
    "EDGE_DIRECTION_UNKNOWN"
  ]),
  severity: z.enum(["error", "warning"]),
  subject: SystemMapWarningSubjectSchema,
  message: z.string()
});

export const SystemMapIntegritySchema = z.object({
  status: z.enum(["OK", "DEGRADED"]),
  warnings: z.array(SystemMapWarningSchema)
});

/**
 * What the map was built from.
 *
 * Reported because "0 edges" and "0 workflows" look identical on a canvas, and
 * an empty map with a healthy `OK` status is otherwise indistinguishable from a
 * read that silently returned nothing.
 */
export const RoleSystemMapProvenanceSchema = z.object({
  workflowsRead: z.number().int().nonnegative(),
  nodesRead: z.number().int().nonnegative(),
  /** Workflows whose edge set is incomplete. The honest denominator for the UI's copy. */
  workflowsWithIncompleteEdgeSet: z.number().int().nonnegative()
});

export const RoleSystemMapViewSchema = z.object({
  organizationId: z.string(),
  nodes: z.array(RoleSystemMapNodeSchema),
  edges: z.array(RoleSystemMapEdgeSchema),
  integrity: SystemMapIntegritySchema,
  provenance: RoleSystemMapProvenanceSchema
});

export type RoleSystemMapView = z.infer<typeof RoleSystemMapViewSchema>;

/**
 * Carry a domain integrity record across the wire boundary.
 *
 * The domain type holds `readonly` arrays because nothing downstream of
 * `mergeSystemMapIntegrity` may mutate a rollup. The wire type is plain JSON
 * and holds mutable ones. A readonly array is not assignable to a mutable one —
 * correctly — so the two meet HERE, in one named function, rather than at every
 * assembly site through a cast.
 *
 * The alternative was `.readonly()` on the schema, which in Zod 4 FREEZES the
 * parsed value at runtime. The generated client parses every response, so that
 * would hand the frontend a frozen object and turn any innocent local mutation
 * into a `TypeError` in production. A six-line copy is the cheaper trade.
 */
export function toWireIntegrity(integrity: SystemMapIntegrity): RoleSystemMapView["integrity"] {
  return {
    status: integrity.status,
    warnings: integrity.warnings.map((warning) => ({ ...warning }))
  };
}
